import { observer } from 'mobx-react'
import type { CSSProperties } from 'react'
import { JSX, useContext, useEffect, useState } from 'react'
import { Button, Header, Icon, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { API_URL, ONBOARDING_SERVICE_PATH } from '@constants/apiUrl'
import {
  ErrorTrans,
  VerifiedErrorReasonTypes,
  GetFailureDetailstResponse,
  ResumeFromFailureScreenResponse,
} from '@/constants/verifiedTypes'
import { Illustration } from '@/components/Illustration/Illustration'

const FailureIcon = ({
  type,
  step,
  color,
}: {
  type: VerifiedErrorReasonTypes
  step: string
  color: string
}): JSX.Element | undefined => {
  const component: Record<string, Record<string, JSX.Element>> = {
    'FaceSpoofing.Detected': {
      step1: <Icon type="change" color={color} size="32px" />,
    },
    'FacesMismatch.Detected': {
      step1: <Icon type="document" color={color} size="32px" />,
      step2: <Icon type="id" color={color} size="32px" />,
      step3: <Icon type="change" color={color} size="32px" />,
    },
    'Luminance.TooLow': {
      step1: <Icon type="bulb" color={color} size="32px" />,
      step2: <Icon type="change" color={color} size="32px" />,
    },
    'Liveness.NotDetected': {
      step1: <Icon type="emoji-smile" color={color} size="32px" />,
    },
    'Document.Illegible': {
      step1: <Icon type="camera" color={color} size="32px" />,
      step2: <Icon type="change" color={color} size="32px" />,
    },
    'Document.NotLegible': {
      step1: <Icon type="camera" color={color} size="32px" />,
      step2: <Icon type="change" color={color} size="32px" />,
    },
    'Document.NotSecurityElements': {
      step1: <Icon type="document" color={color} size="32px" />,
      step2: <Icon type="id" color={color} size="32px" />,
    },
    'Document.Expired': {
      step1: <Icon type="calendar" color={color} size="32px" />,
    },
    'Document.Aspect': {
      step1: <Icon type="document" color={color} size="32px" />,
      step2: <Icon type="id" color={color} size="32px" />,
    },
    'User.CorroborationFailed': {},
    default: {
      step1: <Icon type="change" color={color} size="32px" />,
      step2: <Icon type="globe" color={color} size="32px" />,
      step3: <Icon type="camera" color={color} size="32px" />,
      step4: <Icon type="monitor" color={color} size="32px" />,
    },
  }

  return component[type] ? component[type][step] : component['default'][step]
}

export interface VerifiedErrorEnpalPageProps {
  errorReason: VerifiedErrorReasonTypes
}

export const VerifiedErrorEnpalPage = observer(({ errorReason }: VerifiedErrorEnpalPageProps) => {
  const store = useContext(StoreContext)
  const { verifiedErrorPage: trans } = store.TranslationsState.translations
  const { isMobile } = store.AppState
  const { width, height } = store.AppState.screenSize

  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [failureReason, setFailureReason] = useState<VerifiedErrorReasonTypes>('default')
  const [errorTrans, setErrorTrans] = useState<ErrorTrans>(trans.default)
  const [expectedFullName, setExpectedFullName] = useState('')
  const [actualFullName, setActualFullName] = useState('')
  const [allowRetryFromFailureScreen, setAllowRetryFromFailureScreen] = useState(false)

  // Mobile utilities
  const [mobileContainerHeight, setMobileContainerHeight] = useState('')

  const handleProceed = async () => {
    if (!btnLoading) {
      setBtnLoading(true)

      try {
        const { data } = await axiosInstance.get<ResumeFromFailureScreenResponse>(
          `${API_URL}/${ONBOARDING_SERVICE_PATH}/resume-from-failure-screen`,
          {
            withCredentials: true,
          }
        )

        forceRedirect(data.resumeUrl)
      } catch (error) {
        devLog('Error fetching resume from failure screen:', error)
      }

      setIsLoading(false)
    }
  }

  const getFailureDetails = async () => {
    try {
      const { data } = await axiosInstance.get<GetFailureDetailstResponse>(
        `${API_URL}/${ONBOARDING_SERVICE_PATH}/get-failure-details`,
        {
          withCredentials: true,
        }
      )

      setAllowRetryFromFailureScreen(!!data.allowRetryFromFailureScreen)

      if (data.providers?.signature && data.providers?.signature?.failureReason) {
        const signatureFailureReason = data.providers?.signature?.failureReason
        setErrorTrans(
          signatureFailureReason && trans[signatureFailureReason]
            ? trans[signatureFailureReason]
            : trans['default']
        )
        setFailureReason(signatureFailureReason)

        if (data.providers?.corroboration?.firstName && data.providers?.corroboration?.lastName) {
          setExpectedFullName(
            `${data.providers.corroboration.firstName.expected} ${data.providers.corroboration.lastName.expected}`
          )
          setActualFullName(
            `${data.providers.corroboration.firstName.actual} ${data.providers.corroboration.lastName.actual}`
          )
        }
      } else {
        setFailureReason(errorReason)
        setErrorTrans(errorReason && trans[errorReason] ? trans[errorReason] : trans['default'])
      }
    } catch (error) {
      devLog('Error fetching failure details:', error)

      setErrorTrans(errorReason && trans[errorReason] ? trans[errorReason] : trans['default'])
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (width && width <= 980) {
      setMobileContainerHeight(`calc(${window.innerHeight}px - 120px)`)
    } else {
      setMobileContainerHeight('')
    }
  }, [width, height])

  useEffect(() => {
    getFailureDetails()
  }, [])

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0">
      <div
        className="layout__content-container-with-scroll"
        style={
          isMobile
            ? ({ '--ccws-mobile-height': mobileContainerHeight } as CSSProperties)
            : undefined
        }
      >
        <Illustration type="identity-verification-failed" />
        <Header>{errorTrans.title}</Header>
        {errorTrans.yourName && (
          <>
            <p className="generic__paragraph m-10-0">{errorTrans.yourName}</p>
            <div className="enpal__name-wrapper">{actualFullName}</div>
          </>
        )}

        {errorTrans.didNotMatch && (
          <>
            <p className="generic__paragraph m-10-0">{errorTrans.didNotMatch}</p>
            <div className="enpal__name-wrapper">{expectedFullName}</div>
            <div className="generic__spacer h-15" />
          </>
        )}

        <div className="enpal__step">
          <div className="enpal__step-icon">
            <Icon type="email" color="var(--icon-main-color, black)" size="32px" />
          </div>
          <div className="enpal__info-text">{errorTrans.description}</div>
        </div>

        {errorTrans.step1 && (
          <>
            <p className="generic__paragraph m-0-0-15">{errorTrans.beforeYouRetry}</p>

            <div className="enpal__step">
              <div className="enpal__step-icon">
                <FailureIcon
                  type={failureReason}
                  step="step1"
                  color="var(--icon-error-color, var(--icon-main-color))"
                />
              </div>
              <div className="enpal__info-text">{errorTrans.step1}</div>
            </div>
          </>
        )}

        {errorTrans.step2 && (
          <div className="enpal__step">
            <div className="enpal__step-icon">
              <FailureIcon
                type={failureReason}
                step="step2"
                color="var(--icon-error-color, var(--icon-main-color))"
              />
            </div>
            <div className="enpal__info-text">{errorTrans.step2}</div>
          </div>
        )}

        {errorTrans.step3 && (
          <div className="enpal__step">
            <div className="enpal__step-icon">
              <FailureIcon
                type={failureReason}
                step="step3"
                color="var(--icon-error-color, var(--icon-main-color))"
              />
            </div>
            <div className="enpal__info-text">{errorTrans.step3}</div>
          </div>
        )}

        {errorTrans.step4 && (
          <div className="enpal__step">
            <div className="enpal__step-icon">
              <FailureIcon
                type={failureReason}
                step="step4"
                color="var(--icon-error-color, var(--icon-main-color))"
              />
            </div>
            <div className="enpal__info-text">{errorTrans.step4}</div>
          </div>
        )}

        {isMobile && allowRetryFromFailureScreen && <div className="layout__margined m-0-0-60" />}
      </div>

      {allowRetryFromFailureScreen && (
        <div className="layout__content-container-bottom-fixed-centered">
          <div className="layout__vertically-centered-container">
            <Button
              onClick={handleProceed}
              disabled={btnLoading}
              tabIndex={0}
              className="button--proceed"
            >
              <span className="generic__button-text" data-testId="proceed">
                {errorTrans.tryAgain}
              </span>
            </Button>
          </div>
        </div>
      )}
    </div>
  )
})
